import HeadlineLeadComponents from '@components/HeadlineLead'
import Social from '@components/Social'
import config from '@config'
import usePageMetadata from '@hooks/usePageMetadata'
import { APICommentingData } from '@widgets/Commenting/types'
import Timestamps from '@components/Timestamps'
import { CookWidget } from '@widgets/types'
import React, {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { InfiniteData, useQueryClient } from 'react-query'
import { HeadlineLeadProps } from './types'
import { stripHtml } from '@hooks/useTracking/utils'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useArticleSchema from '@hooks/useArticleSchema'
import styled from 'styled-components'

const {
  publication: { publication },
} = config

const {
  ArticleHeader,
  ArticleMetadata,
  ArticleLead,
  CommentButton,
  TitleCatchword,
  SharingAndCommentWrapper,
} = HeadlineLeadComponents

const StyledTimestamps = styled(Timestamps)`
  margin: 8px 0;
`

const HeadlineLead: FunctionComponent<HeadlineLeadProps> = ({
  catchword,
  title,
  lead,
  displayCommentsOnArticle,
}) => {
  const [commentsNumber, setCommentsNumber] = useState<number>(0)
  const unsubscribeFnRef = useRef<() => void>()
  const pageMetadata = usePageMetadata()
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const articleClickShowCommentHandler = useCallback<TrackingFnType>(
    ({ pageMetadata }) => {
      return {
        event: 'article_show_comments',
        eventCategory: 'article',
        eventAction: 'show_comments',
        eventLabel: `${pageMetadata.id}:${stripHtml(pageMetadata.title)}`,
      }
    },
    []
  )

  const handleArticleClickShowCommentTracking = useTracking(
    articleClickShowCommentHandler
  )

  const {
    hasPlaylist,
    publishedDate,
    lastModifiedDate,
    id: articleId,
  } = pageMetadata

  const updateCommentsNumberData = useCallback(
    (args: any) => {
      if (
        args.type === 'queryUpdated' &&
        args.query.queryKey?.[0] === 'commentingData' &&
        args.query.queryKey?.[1] === articleId
      ) {
        const data = queryClient.getQueryData<InfiniteData<APICommentingData>>([
          'commentingData',
          articleId,
        ])

        const numberOfComments = data?.pages?.[0]?.number_of_comments ?? 0

        setCommentsNumber(numberOfComments)
      }
    },
    [articleId, queryClient]
  )

  const scrollToCommentingWidget = useCallback(() => {
    const commentingWidgetRef = queryClient.getQueryData<
      RefObject<HTMLDivElement>
    >('commenting-widget-ref')
    handleArticleClickShowCommentTracking()

    if (!commentingWidgetRef) {
      return
    }

    try {
      commentingWidgetRef.current?.scrollIntoView({ behavior: 'smooth' })
    } catch {
      commentingWidgetRef.current?.scrollIntoView()
    }
  }, [queryClient, handleArticleClickShowCommentTracking])

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateCommentsNumberData)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, updateCommentsNumberData])

  useArticleSchema({ description: lead })

  return (
    <ArticleHeader>
      <TitleCatchword title={title} catchword={catchword}>
        {!!lead && <ArticleLead>{lead}</ArticleLead>}
      </TitleCatchword>
      <ArticleMetadata>
        <StyledTimestamps
          publishedDate={publishedDate}
          updatedDate={lastModifiedDate}
        />
        <SharingAndCommentWrapper>
          <Social endOfArticle={false} />
          {publication !== 'romandie' &&
            displayCommentsOnArticle &&
            !hasPlaylist && (
              <CommentButton onClick={scrollToCommentingWidget}>
                {commentsNumber}
              </CommentButton>
            )}
        </SharingAndCommentWrapper>
      </ArticleMetadata>
    </ArticleHeader>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'headline-lead'],
  component: HeadlineLead,
}

export default widget
